import useItemsCategories from '@app/hooks/general/useItemsCategories';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatHSCode } from '@/utils/general';
import {
  CategoryHSOption,
  CategoryHSCodeSelectionProps,
  CategoryHSCodeSelectionValue,
  CategoryHSSuggestedOption,
  CategoryHSCategoryOption,
} from '../types';
import { usePortal } from './usePortal';
import { addHSCode } from '../context/store';
import { useSelectedCodes } from './useSelectedCodes';

const sortByFullName = (a: CategoryHSOption, b: CategoryHSOption) => {
  const nameA = (a.fullName || a.label || '').toLowerCase();
  const nameB = (b.fullName || b.label || '').toLowerCase();
  return nameA.localeCompare(nameB);
};

export const isCategoryHSCategoryOption = (value: unknown): value is CategoryHSCategoryOption =>
  !!(value as CategoryHSCategoryOption)?.id;

export const isCategoryHSSuggestedOption = (value: unknown): value is CategoryHSSuggestedOption =>
  !!(value as CategoryHSSuggestedOption)?.code;

export function useCategoryHSCode(
  onChange?: CategoryHSCodeSelectionProps['onChange'],
  value?: CategoryHSCodeSelectionProps['value'],
  is10DigitCodeEnabled?: boolean
) {
  const categories = useItemsCategories();
  const selectedCodes = useSelectedCodes();

  const [selectedValue, setSelectedValue] = useState<CategoryHSCodeSelectionValue | null>(null);

  const refSelectedValue = useRef(selectedValue);
  useEffect(() => {
    refSelectedValue.current = selectedValue;
  }, [selectedValue]);

  const {
    portalPosition,
    setRef,
    open,
    closeDropDown,
    toggleOpen,
    setPortalRef,
    componentState,
    setComponentStateDropdown,
    setComponentStateSearch,
    portalRef,
    openDropDown,
    parentRef,
  } = usePortal();

  const refOnChange = useRef(onChange);
  useEffect(() => {
    refOnChange.current = onChange;
  }, [onChange]);

  const setSelectedValueByDropDown = useCallback(
    (value: CategoryHSCodeSelectionValue) => {
      setSelectedValue(value);
      closeDropDown();
      refOnChange.current?.(value);
      parentRef.current?.focus();
    },
    [closeDropDown, parentRef]
  );

  const setSelectedValueBySearch = useCallback(
    (value: CategoryHSCodeSelectionValue) => {
      setSelectedValue(value);
      addHSCode(value.value, (value as CategoryHSSuggestedOption).description || '');
      closeDropDown();
      refOnChange.current?.(value);
      parentRef.current?.focus();
    },
    [closeDropDown, parentRef]
  );

  const categoryOptions = useMemo(() => {
    const selectedCodesOptions = selectedCodes.map((code) => {
      const label = `${formatHSCode(code.code)}${code.description ? ` - ${code.description}` : ''}`;
      return {
        ...code,
        value: code.code,
        label,
        fullName: label,
      } as unknown as CategoryHSCodeSelectionValue;
    });

    const selectedCategoriesOptions = categories.map(
      (category) =>
        ({
          value: category.id,
          label: category.fullName || category.name,
          ...category,
        } as unknown as CategoryHSCodeSelectionValue)
    );

    return [
      ...(is10DigitCodeEnabled
        ? selectedCodesOptions
        : selectedCodesOptions.filter((s) => !!(s as CategoryHSSuggestedOption).description)),
      ...selectedCategoriesOptions,
    ].sort(sortByFullName);
  }, [categories, selectedCodes, is10DigitCodeEnabled]);

  const refCategoryOptions = useRef(categoryOptions);
  useEffect(() => {
    refCategoryOptions.current = categoryOptions;
  }, [categoryOptions]);

  useEffect(() => {
    if (isCategoryHSCategoryOption(value)) {
      if (value.id === (refSelectedValue.current as CategoryHSCategoryOption)?.id) return;
      const isExists = categories.find((category) => category.id === value.id);
      if (!isExists) return;
      const selectedValue = refCategoryOptions.current.find(
        (option) => (option as CategoryHSCategoryOption).id === value.id
      );
      if (selectedValue) {
        setSelectedValue(selectedValue);
      }
      return;
    }

    if (isCategoryHSSuggestedOption(value)) {
      if (value.code === (refSelectedValue.current as unknown as CategoryHSSuggestedOption)?.code)
        return;

      const label = `${formatHSCode(value.code)}${
        value.description ? ` - ${value.description}` : ''
      }`;
      const obj = {
        description: value.description,
        code: value.code,
        value: value.code,
        label,
        fullName: label,
      };
      setSelectedValue(obj);
      addHSCode(value.code, value.description || '');
    }
  }, [value, categories]);

  return {
    open,
    toggleOpen,
    closeDropDown,
    setRef,
    setSelectedValueByDropDown,
    selectedValue,
    portalPosition,
    categoryOptions,
    componentState,
    setComponentStateDropdown,
    setComponentStateSearch,
    setPortalRef,
    setSelectedValueBySearch,
    portalRef,
    openDropDown,
    is10DigitCodeEnabled,
  };
}
